/**
 * The recording's audio for the player (UC-8, light). It sits behind the auth-gated files
 * route, which an `<audio src>` cannot reach, so it is fetched with the operator's token into a
 * blob and played from an object URL. Each fetch is one `file.read` row, so it happens once per
 * URL. The files route answers every file as `application/octet-stream`; the blob is given the
 * audio type its extension names, or Safari will not play it.
 */
import { useEffect, useState } from 'react'
import type { RecordingData } from '@/components/humint/questioning/recording-types'
import { humintRecordingsApi } from '@/services/api/humint-recordings'
import { asPlayable } from '@/utils/audio-playback'

export type RecordingAudio =
  /** The view names no audio. */
  | { status: 'none'; src: null }
  | { status: 'loading'; src: null }
  | { status: 'ready'; src: string }
  /** It could not be fetched; the transcript stays readable. `error` is what the fetch threw. */
  | { status: 'failed'; src: null; error?: unknown }

const NONE: RecordingAudio = { status: 'none', src: null }
const LOADING: RecordingAudio = { status: 'loading', src: null }

/**
 * The file the page fetches for the player, or null while a better one is about to replace it.
 * The worker renders the playback copy before it transcribes, so a recording still processing
 * with neither a copy nor a transcript is about to get its copy: fetching the recording as
 * received then would download it — and write its `file.read` row — only to do both again for
 * the copy moments later. Once there is a transcript or the job has ended, no copy is coming and
 * the recording as received is what plays.
 */
export function audioToFetch(
  view: Pick<RecordingData, 'audioUrl' | 'audioSource' | 'state' | 'transcript'>
): string | null {
  const copyComing =
    view.audioSource !== 'playback' &&
    view.state === 'processing' &&
    view.transcript.status === 'pending'
  return copyComing ? null : view.audioUrl
}

/** What the last fetch that answered brought, and for which URL. */
export interface LoadedAudio {
  url: string
  audio: RecordingAudio
}

/** The audio the player gets for `audioUrl`: what was fetched for it, once it is here. */
export function audioFor(audioUrl: string | null, loaded: LoadedAudio | null): RecordingAudio {
  if (!audioUrl) return NONE
  return loaded?.url === audioUrl ? loaded.audio : LOADING
}

/**
 * `audioUrl` is the `/api/v1/…` path to fetch (`audioToFetch`), or null while there is none.
 * `typedAs`: a file name whose suffix names the audio type when the URL itself does not (a
 * clip route ending in `/audio` plays a WAV).
 */
export function useRecordingAudio(audioUrl: string | null, typedAs?: string): RecordingAudio {
  const [loaded, setLoaded] = useState<LoadedAudio | null>(null)

  useEffect(() => {
    if (!audioUrl) return
    const controller = new AbortController()
    humintRecordingsApi
      .audio(audioUrl, controller.signal)
      .then(blob => {
        if (controller.signal.aborted) return
        const src = URL.createObjectURL(asPlayable(blob, typedAs ?? audioUrl))
        setLoaded({ url: audioUrl, audio: { status: 'ready', src } })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setLoaded({ url: audioUrl, audio: { status: 'failed', src: null, error } })
      })
    return () => controller.abort()
  }, [audioUrl, typedAs])

  // An object URL lives until another replaces it or the page goes.
  const src = loaded?.audio.src ?? null
  useEffect(
    () => () => {
      if (src) URL.revokeObjectURL(src)
    },
    [src]
  )

  return audioFor(audioUrl, loaded)
}
